import { useState, useEffect, type Dispatch, type SetStateAction } from "react";
import { toDate } from "../utils/dateUtils";
import type { LatestCobResponse } from "../types/api";

const baseUrl: string = process.env.REACT_APP_API_BASE_URL ?? "";

/**
 * Hook that fetches the latest COB date from a given endpoint,
 * then lets the user override it.
 *
 * @param endpoint  e.g. "/pnl_latest_cob"
 * @returns [cobDate, setCobDate, loading]
 */
export default function useCobDate(
  endpoint: string,
): [Date | null, Dispatch<SetStateAction<Date | null>>, boolean, Date | null] {
  const [cobDate, setCobDate] = useState<Date | null>(null);
  const [latestCobDate, setLatestCobDate] = useState<Date | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (cobDate !== null) return;

    const ac = new AbortController();
    setLoading(true);

    (async () => {
      try {
        const r = await fetch(`${baseUrl}${endpoint}`, { signal: ac.signal });
        const json = await r.json();
        const d = toDate(json.value ?? json.cob_date ?? json.latest_cob);
        setCobDate(d);
        setLatestCobDate(d);
      } catch (e: unknown) {
        if (e instanceof Error && e.name !== "AbortError") console.error(e);
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [cobDate, endpoint]);

  return [cobDate, setCobDate, loading, latestCobDate];
}
